import React from 'react';
import Animation from '../../Animation';
import Select from '../../engine/SelectEngine';
import InputBase from '../../form/InputBase';
import styled from 'styled-components/macro';
import { DatePicker } from '@material-ui/pickers';
import moment from 'moment';
import { NumberInput } from '../../../internal-components/form-builder/view/RemindersStyledInputs';
import useTranslations from '../../../hooks/useTranslations';

const AlignmentSpan = styled.span`
  display: flex;
  align-items: center;

  & > :nth-child(2) {
    margin-left: 0.8rem;
  }

  ${({ $customCSS }) => $customCSS}
`;

const SpacingContainer = styled.span`
  margin: 0 0.8rem;
`;

const DateComparisonView = props => {
  const t = useTranslations();
  return (
    <AlignmentSpan>
      <Animation in={props.showAnytimeDropdown} type="collapse">
        <AlignmentSpan>
          <Select
            data-testid="logicbuilder-text-comparison-select-anytime-dropdown"
            variant="inline"
            onChange={props.onAnytimeDropdownChanged}
            value={props.anytimeDropdownValue}
            options={[
              { text: 'anytime', value: 'anytime' },
              { text: 'more than', value: 'more' },
              { text: 'less than', value: 'less' },
              { text: 'between', value: 'between' }
            ]}
          />
        </AlignmentSpan>
      </Animation>
      <Animation in={props.showAnytimeStaticWord} type="collapse">
        <AlignmentSpan>anytime</AlignmentSpan>
      </Animation>
      <Animation in={props.showFirstInterval} type="collapse">
        <AlignmentSpan>
          <NumberInput
            data-testid="first-interval-count"
            onChange={props.onFirstIntervalCountChanged}
            onBlur={() => {}}
            type="number"
            value={props.firstIntervalCount}
          />
          <Select
            data-testid="first-interval-unit"
            onChange={props.onFirstIntervalUnitChanged}
            value={props.firstIntervalUnit}
            variant="inline"
            options={[
              {
                value: 'day',
                text: t('form-builder.reminders.days', {
                  count: props.firstIntervalCount || 0
                }),
                'data-testid': 'first-interval-unit-day'
              },
              {
                value: 'week',
                text: t('form-builder.reminders.weeks', {
                  count: props.firstIntervalCount || 0
                }),
                'data-testid': 'first-interval-unit-week'
              },
              {
                value: 'month',
                text: t('form-builder.reminders.months', {
                  count: props.firstIntervalCount || 0
                }),
                'data-testid': 'first-interval-unit--month'
              }
            ]}
          />
        </AlignmentSpan>
      </Animation>
      <Animation in={props.showSecondInterval} type="collapse">
        <AlignmentSpan>
          and
          <NumberInput
            data-testid="second-interval-count"
            onChange={props.onSecondIntervalCountChanged}
            onBlur={() => {}}
            type="number"
            value={props.secondIntervalCount}
          />
          <Select
            data-testid="second-interval-unit"
            onChange={props.onSecondIntervalUnitChanged}
            value={props.secondIntervalUnit}
            variant="inline"
            options={[
              {
                value: 'day',
                text: t('form-builder.reminders.days', {
                  count: props.secondIntervalCount || 0
                }),
                'data-testid': 'second-interval-unit-day'
              },
              {
                value: 'week',
                text: t('form-builder.reminders.weeks', {
                  count: props.secondIntervalCount || 0
                }),
                'data-testid': 'second-interval-unit-week'
              },
              {
                value: 'month',
                text: t('form-builder.reminders.months', {
                  count: props.secondIntervalCount || 0
                }),
                'data-testid': 'second-interval-unit--month'
              }
            ]}
          />
        </AlignmentSpan>
      </Animation>
      <SpacingContainer>
        <Select
          data-testid="logicbuilder-text-comparison-dropdown"
          variant="inline"
          onChange={props.onComparisonDropdownChanged}
          value={props.comparisonDropdownValue}
          placeholder="Choose an option..."
          options={[
            {
              text: 'logic.blank',
              value: 'blank',
              'data-testid': `logicbuilder-date-comparison-dropdown-option-blank`
            },
            {
              text: 'logic.not_blank',
              value: 'not_blank',
              'data-testid': `logicbuilder-date-comparison-dropdown-option-not_blank`
            },
            {
              text: 'in the future',
              value: 'future',
              'data-testid': `logicbuilder-date-comparison-dropdown-option-future`
            },
            {
              text: 'in the past',
              value: 'past',
              'data-testid': `logicbuilder-date-comparison-dropdown-option-past`
            },
            {
              text: 'after (>)',
              value: '>',
              'data-testid': `logicbuilder-date-comparison-dropdown-option->`
            },
            {
              text: 'on or after (>=)',
              value: '>=',
              'data-testid': `logicbuilder-date-comparison-dropdown-option->=`
            },
            {
              text: 'on (=)',
              value: '=',
              'data-testid': `logicbuilder-date-comparison-dropdown-option-=`
            },
            {
              text: 'on or before (<=)',
              value: '<=',
              'data-testid': `logicbuilder-date-comparison-dropdown-option-<=`
            },
            {
              text: 'before (<)',
              value: '<',
              'data-testid': `logicbuilder-number-comparison-dropdown-option-<`
            },
            {
              text: 'between',
              value: 'between',
              'data-testid': `logicbuilder-date-comparison-select-option-between`
            }
          ]}
        />
      </SpacingContainer>
      <Animation in={props.showFirstDate} type="collapse">
        <AlignmentSpan>
          <DatePicker
            autoOk
            TextFieldComponent={InputBase}
            variant={props.variant}
            inputVariant={props.variant}
            format={moment.localeData().longDateFormat('L')}
            // mask={props.dateFormat}
            value={props.firstDate ? props.parseDate(props.firstDate) : null}
            emptyLabel="Select date"
            onChange={props.onFirstDatePickerChanged}
          />
        </AlignmentSpan>
      </Animation>
      <Animation in={props.showSecondDate} type="collapse">
        <AlignmentSpan>
          <SpacingContainer>and</SpacingContainer>
          <DatePicker
            autoOk
            TextFieldComponent={InputBase}
            variant={props.variant}
            inputVariant={props.variant}
            format={moment.localeData().longDateFormat('L')}
            // mask={props.dateFormat}
            value={props.secondDate ? props.parseDate(props.secondDate) : null}
            emptyLabel="Select date"
            onChange={props.onSecondDatePickerChanged}
          />
        </AlignmentSpan>
      </Animation>
    </AlignmentSpan>
  );
};

export default DateComparisonView;
